/**
 * Yuqori paneldagi widget joyi (docs/11-widgetlar.md, 2-bo'lim):
 * - ixcham ko'rinish (tanlangan widgetlar) yoki faqat ikon -- yuqori panelda hech narsa tanlanmagan bo'lsa;
 * - sichqoncha borsa (150ms) panel silliq chiqadi, ketsa (300ms) yopiladi; bosilsa yoki ichida biror amal qilinsa --
 *   ochiq qoladi (tashqariga bosish yoki Esc yopadi);
 * - qadalgan: panel o'ng tomonda ustun (WidgetRail), faqat keng ekranda; shu qurilmada eslab qolinadi.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutGrid } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/misc";
import { useWidgetLayout } from "./api";
import { glanceItems, normalize } from "./layout";
import { setPinned, usePinned, useWideScreen } from "./pinned";
import { WIDGETS } from "./registry";
import { WidgetPanel } from "./WidgetPanel";

const OPEN_DELAY = 150;
const CLOSE_DELAY = 300;
const EXIT_MS = 200;

type OpenState = false | "peek" | "sticky";

export const widgetsEnabled = (me: Me) => me.role !== "carrier";

function useLayout(me: Me) {
  const q = useWidgetLayout(widgetsEnabled(me));
  const layout = useMemo(() => (q.data ? normalize(q.data, WIDGETS, me) : null), [q.data, me]);
  return { q, layout };
}

/** Yopilish animatsiyasi tugaguncha panel DOM'da qoladi */
function usePresence(open: boolean): boolean {
  const [mounted, setMounted] = useState(open);
  if (open && !mounted) setMounted(true);
  useEffect(() => {
    if (open) return;
    const timer = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [open]);
  return mounted || open;
}

/** Qadalgan ustun ko'rinadimi (AppShell kontent yonida chizadi) */
export function useWidgetRail(me: Me): boolean {
  const pinned = usePinned();
  const wide = useWideScreen();
  return pinned && wide && widgetsEnabled(me);
}

function PanelBody({ me, q, layout, pinned, canPin, onPin }: { me: Me; q: ReturnType<typeof useLayout>["q"]; layout: ReturnType<typeof useLayout>["layout"]; pinned: boolean; canPin: boolean; onPin: () => void }) {
  const { t } = useTranslation("widgets");
  if (layout) return <WidgetPanel me={me} layout={layout} pinned={pinned} canPin={canPin} onPin={onPin} />;
  if (q.isError) {
    return (
      <div className="px-2 py-6 text-center text-[13px] text-ink-soft">
        {t("loadFailed")}
        <div className="mt-3">
          <Button size="sm" onClick={() => q.refetch()}>
            {t("retry")}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-center py-8">
      <Spinner />
    </div>
  );
}

export function WidgetDock({ me }: { me: Me }) {
  const { t } = useTranslation("widgets");
  const { q, layout } = useLayout(me);
  const wide = useWideScreen();
  const rail = useWidgetRail(me);
  const [open, setOpen] = useState<OpenState>(false);
  const shown = !!open && !rail;
  const mounted = usePresence(shown);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const timers = useRef<{ open?: number; close?: number }>({});

  const clearTimers = () => {
    window.clearTimeout(timers.current.open);
    window.clearTimeout(timers.current.close);
  };
  useEffect(() => clearTimers, []);

  useEffect(() => {
    if (!shown) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (!target || wrap.current?.contains(target)) return;
      // O'ng tugma menyusi, tasdiqlash oynasi va xabar ("Qaytarish") panel ichidagi amal hisoblanadi
      if (target.closest('[role="menu"], [role="dialog"], [role="alertdialog"], [role="status"]')) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"]')) return;
      const inside = wrap.current?.contains(document.activeElement);
      setOpen(false);
      if (inside) trigger.current?.focus();
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [shown]);

  if (!widgetsEnabled(me)) return null;
  const glance = layout ? glanceItems(layout) : [];

  return (
    <div
      ref={wrap}
      className="relative"
      onPointerEnter={(e) => {
        if (e.pointerType !== "mouse" || rail) return;
        window.clearTimeout(timers.current.close);
        if (!open) timers.current.open = window.setTimeout(() => setOpen((o) => o || "peek"), OPEN_DELAY);
      }}
      onPointerLeave={(e) => {
        if (e.pointerType !== "mouse") return;
        window.clearTimeout(timers.current.open);
        if (open === "peek") timers.current.close = window.setTimeout(() => setOpen((o) => (o === "peek" ? false : o)), CLOSE_DELAY);
      }}
    >
      <button
        ref={trigger}
        type="button"
        onClick={() => {
          clearTimers();
          if (rail) setPinned(false);
          else setOpen((o) => (o === "sticky" ? false : "sticky"));
        }}
        aria-expanded={shown || rail}
        aria-haspopup="dialog"
        aria-label={t("title")}
        title={t("title")}
        className={cn(
          "flex h-10 items-center gap-3 rounded-full bg-fill px-4 text-ink transition-colors hover:bg-fill-hover",
          (shown || rail) && "bg-raised shadow-pill",
          glance.length === 0 && "w-10 justify-center px-0 text-ink-soft",
        )}
      >
        {glance.length ? (
          glance.map((item, i) => {
            const Glance = WIDGETS[item.type].Glance;
            return Glance ? (
              <Fragment key={item.id}>
                {i > 0 && <span className="hidden h-4 w-px bg-line lg:block" aria-hidden />}
                <Glance item={item} me={me} />
              </Fragment>
            ) : null;
          })
        ) : (
          <LayoutGrid size={16} strokeWidth={1.75} aria-hidden />
        )}
      </button>
      {mounted && (
        <div
          className="widget-pop absolute top-full right-0 z-50 pt-2"
          data-open={shown}
          role="dialog"
          aria-label={t("title")}
          // Ichida biror amal -- panel ochiq qoladi (sichqoncha chiqsa ham)
          onPointerDownCapture={() => setOpen("sticky")}
          onContextMenuCapture={() => setOpen("sticky")}
          onFocusCapture={() => setOpen((o) => o && "sticky")}
        >
          <div className="max-h-[calc(100vh-80px)] w-[332px] overflow-y-auto overscroll-contain rounded-[26px] border border-line bg-surface/90 p-4 shadow-menu backdrop-blur-2xl">
            <PanelBody
              me={me}
              q={q}
              layout={layout}
              pinned={false}
              canPin={wide}
              onPin={() => {
                setOpen(false);
                setPinned(true);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** Qadalgan panel: kontent yonida, sahifa bilan birga aylanmaydi */
export function WidgetRail({ me }: { me: Me }) {
  const { t } = useTranslation("widgets");
  const { q, layout } = useLayout(me);
  return (
    <aside className="widget-rail w-[333px] shrink-0 overflow-x-hidden border-l border-line bg-sidebar" aria-label={t("title")}>
      {/* Ustun kontent bo'yi cho'ziladi, ichi ekranga yopishadi: sahifaga ortiqcha aylantirish qo'shmaydi */}
      <div className="sticky top-0 max-h-screen w-[332px] overflow-y-auto overscroll-contain p-4">
        <PanelBody me={me} q={q} layout={layout} pinned canPin onPin={() => setPinned(false)} />
      </div>
    </aside>
  );
}
